import { action, observable, runInAction } from 'mobx';
import { DocumentContext, Message } from '../types';

/** Where the user currently is in the app — injected into the agent prompt. */
export interface PolicyAssistantPageContext {
  /** Breadcrumb trail, e.g. "Home > Risk Policies > Data Access Policy". */
  breadcrumb?: string;
  documentTitle?: string;
  documentType?: string;
  documentId?: string;
}

/**
 * Lightweight store for the global Policy Assistant chat.
 *
 * Deliberately NOT the Smart Editor `GenAIAssistantStore` — this is plain
 * markdown Q&A with no batch/accept/undo/section machinery. It keeps a small
 * local message list and streams responses through the shared `LiveAIService`
 * using the `policy-assistant` agent (raw markdown forward).
 */
export class PolicyAssistantStore {
  @observable messages: Message[] = [];
  @observable inputValue = '';
  @observable isLoading = false;
  @observable isOpen = false;
  @observable error: string | null = null;
  /** Timestamps powering the shared TypingIndicator phases. */
  @observable loadingStartTime: number | null = null;
  @observable lastTokenTime: number | null = null;

  private sessionId: string | undefined;
  private readonly userName: string | undefined;

  constructor(userName?: string) {
    this.userName = userName;
  }

  @action setOpen(open: boolean): void {
    this.isOpen = open;
  }

  @action toggleOpen(): void {
    this.isOpen = !this.isOpen;
  }

  @action setInputValue(value: string): void {
    this.inputValue = value;
  }

  @action clear(): void {
    this.messages = [];
    this.inputValue = '';
    this.error = null;
    this.sessionId = undefined;
  }

  private genId(): string {
    return `pa-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  /** Minimal context — the policy-assistant agent only needs identity + location. */
  private buildContext(ctx?: PolicyAssistantPageContext): DocumentContext {
    return {
      documentId: ctx?.documentId || '',
      documentType: ctx?.documentType || 'policy-assistant',
      title: ctx?.documentTitle || 'Policy Assistant',
      tags: [],
      allSections: [],
      userName: this.userName,
    };
  }

  /** Build a short, human-readable "where the user is" preamble for the agent. */
  private buildContextBlock(ctx?: PolicyAssistantPageContext): string {
    if (!ctx) return '';
    const lines: string[] = [];
    if (ctx.breadcrumb) lines.push(`Location: ${ctx.breadcrumb}`);
    if (ctx.documentTitle) {
      const meta = [ctx.documentType, ctx.documentId && `id: ${ctx.documentId}`].filter(Boolean).join(', ');
      lines.push(`Currently viewing: ${ctx.documentTitle}${meta ? ` (${meta})` : ''}`);
    }
    return lines.length > 0 ? `[User Context]\n${lines.join('\n')}` : '';
  }

  @action async sendMessage(text: string, pageContext?: PolicyAssistantPageContext): Promise<void> {
    const prompt = (text ?? '').trim();
    if (!prompt || this.isLoading) return;

    const aiId = this.genId();
    this.messages.push({ id: this.genId(), role: 'user', content: prompt, timestamp: Date.now() });
    this.messages.push({ id: aiId, role: 'assistant', content: '', timestamp: Date.now(), isStreaming: true });
    this.inputValue = '';
    this.isLoading = true;
    this.error = null;
    this.loadingStartTime = Date.now();
    this.lastTokenTime = null;

    try {
      const { LiveAIService } = await import('../liveAIService');
      const service = new LiveAIService();

      const onToken = (chunk: string): void => {
        runInAction(() => {
          const m = this.messages.find(msg => msg.id === aiId);
          if (m) m.content += chunk;
          this.lastTokenTime = Date.now();
        });
      };

      const contextBlock = this.buildContextBlock(pageContext);
      const fullPrompt = contextBlock ? `${contextBlock}\n\n${prompt}` : prompt;

      const response = await service.generateResponse(
        fullPrompt,
        this.buildContext(pageContext),
        undefined, // sectionContext
        this.sessionId,
        onToken,
        undefined, // attachedFile
        undefined, // onAction
        'policy-assistant', // agentIdOverride
        undefined, // onActionStart
        prompt, // rawUserMessage (clean user text, no context preamble)
      );

      runInAction(() => {
        const m = this.messages.find(msg => msg.id === aiId);
        if (m) {
          // Fallback to the final content if streaming produced nothing.
          if (response?.content && !m.content) m.content = response.content;
          m.isStreaming = false;
        }
        if (response?.sessionId) this.sessionId = response.sessionId;
        this.isLoading = false;
      });
    } catch (err: any) {
      runInAction(() => {
        const m = this.messages.find(msg => msg.id === aiId);
        if (m) {
          m.isStreaming = false;
          if (!m.content) {
            m.role = 'error';
            m.content = 'Something went wrong. Please try again.';
          }
        }
        this.error = err?.message || 'Unknown error';
        this.isLoading = false;
      });
    }
  }
}
